import { APIError } from '@/lib/api/api';
import type { APIErrorCode, APIFieldError, EventAgeRestriction } from '@/lib/api/apiTypes';

export type ExtractedEvent = {
  raw_date_text?: string | null;
  raw_doors_time_text?: string | null;
  raw_start_time_text?: string | null;
  raw_end_time_text?: string | null;
  raw_price_text?: string | null;
  raw_age_text?: string | null;
  raw_tag_clues?: string[] | null;
  short_factual_summary?: string | null;
  title?: string | null;
  description?: string | null;
  venue_name?: string | null;
  address?: string | null;
  doors_at?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
  age_restriction?: EventAgeRestriction | null;
  tags?: string[] | null;
  event_url?: string | null;
  ticket_url?: string | null;
  is_free?: boolean | null;
  price?: string | null;
  capacity?: number | null;
  confidence: number;
  is_event: boolean;
  rejection_reason?: string | null;
  title_confidence?: number | null;
  venue_confidence?: number | null;
  date_confidence?: number | null;
  time_confidence?: number | null;
  price_confidence?: number | null;
  tags_confidence?: number | null;
};

export type DuplicateResult = {
  event_id?: string | null;
  event_title?: string | null;
  similarity: number;
};

export type ExtractFlyerResponse = {
  extracted: ExtractedEvent;
  duplicate?: DuplicateResult | null;
};

const FLYER_REQUEST_TIMEOUT_MS = 30000;

export async function extractFlyerFromImage({
  requestRaw,
  uri,
  mimeType,
  name,
  venueID,
}: {
  requestRaw: (path: string, init?: RequestInit) => Promise<Response>;
  uri: string;
  mimeType?: string | null;
  name?: string | null;
  venueID?: string | null;
}) {
  const formData = new FormData();
  formData.append('flyer', {
    uri,
    type: mimeType ?? 'image/jpeg',
    name: name ?? 'flyer.jpg',
  } as any);
  if (venueID) {
    formData.append('venue_id', venueID);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FLYER_REQUEST_TIMEOUT_MS);

  try {
    const response = await requestRaw('/events/extract-flyer', {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      const record = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : null;
      throw new APIError(
        typeof record?.error === 'string' ? record.error : 'Unable to read flyer right now.',
        response.status,
        typeof record?.code === 'string' ? (record.code as APIErrorCode) : undefined,
        {
          fieldErrors: normalizeFieldErrors(record?.field_errors),
          requestID: typeof record?.request_id === 'string' ? record.request_id : undefined,
        }
      );
    }

    return payload as ExtractFlyerResponse;
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') {
      throw new Error('Flyer extraction timed out. You can keep creating the event manually.');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

function normalizeFieldErrors(value: unknown): APIFieldError[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const record = entry as Record<string, unknown>;
    if (
      typeof record.field !== 'string' ||
      typeof record.message !== 'string' ||
      typeof record.code !== 'string'
    ) {
      return [];
    }
    return [
      {
        field: record.field,
        code: record.code as APIErrorCode,
        message: record.message,
      },
    ];
  });
}
